import { useState, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionPath } from '../../components/School/inviteToken';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FiMail, FiAlertCircle, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import './Auth.css';

const VerifyEmail = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const { currentUser, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  /* Hide the shared Layout header/footer so the auth screen gets the same
     self-contained, branded shell as the new logged-out homepage. */
  useLayoutEffect(() => {
    document.body.classList.add('auth-active');
    return () => document.body.classList.remove('auth-active');
  }, []);

  // Check if user is already verified
  useEffect(() => {
    if (currentUser?.emailVerified) {
      navigate(subscriptionPath());
    }
  }, [currentUser, navigate]);

  const handleResendEmail = async () => {
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await resendVerificationEmail();
      setSuccess(true);
    } catch (err) {
      if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError('Failed to send verification email. Please try again.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      // Reload the user to get the latest emailVerified status
      await currentUser.reload();
      if (currentUser.emailVerified) {
        // Force token refresh so Firestore security rules see email_verified: true
        await currentUser.getIdToken(true);
        // Sync to Firestore user document
        await updateDoc(doc(db, 'users', currentUser.uid), { emailVerified: true });
        navigate(subscriptionPath());
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setChecking(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="auth-page">
        <main className="auth-main">
          <div className="auth-shell" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--muted)' }}>
              Please <Link to="/login" className="auth-alt-link" style={{ color: 'var(--teal)', fontWeight: 600 }}>log in</Link> to verify your email.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Helmet>
        <title>Verify Your Email - TeachESLPro</title>
        <meta name="description" content="Verify your email address to complete your TeachESLPro account setup." />
      </Helmet>


      <main className="auth-main">
        <div className="auth-shell">
          <div className="auth-head">
            <h1>Verify your <em>email</em></h1>
            <p>We&apos;ve sent a verification email to:</p>
            <p className="auth-email">{currentUser.email}</p>
          </div>

          <div className="auth-card">
            {error && (
              <div className="auth-error">
                <FiAlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="auth-success">
                <FiCheckCircle className="w-5 h-5" />
                <span>Verification email sent! Check your inbox.</span>
              </div>
            )}

            <div className="auth-notice">
              <FiMail className="w-6 h-6" />
              <div>
                <p>Click the link in the email to verify your account. Once verified, you can choose a subscription plan and start accessing resources.</p>
                <p><strong>Important:</strong> Check your spam/junk folder - verification emails often end up there.</p>
                <p className="auth-notice-muted">The email may take a few minutes to arrive.</p>
              </div>
            </div>

            <div className="auth-actions">
              <button
                onClick={handleCheckVerification}
                disabled={checking}
                className="auth-btn auth-btn-primary auth-submit"
              >
                {checking ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} />
                    <span>Checking...</span>
                  </>
                ) : (
                  <span>I&apos;ve verified my email</span>
                )}
              </button>

              <button
                onClick={handleResendEmail}
                disabled={loading}
                className="auth-btn auth-btn-outline auth-submit"
              >
                {loading ? 'Sending...' : 'Resend verification email'}
              </button>
            </div>

            <Link to="/login" className="auth-back">← Back to login</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmail;
